/**
 * /ai — how to use CherryClipz from Claude or ChatGPT (the MCP connector, emo/mcp_server/).
 *
 * The public documentation page the Claude Connectors Directory and the ChatGPT app directory
 * ask for. Same honesty rule as the legal pages: every line describes what the connector really
 * does today (tools in emo/mcp_server/tools.py, the 🔗 AI link in createVideo.jsx). If a tool is
 * added or changed, change this file in the same commit.
 */
import type { LegalSection } from "../legal/legalConstants";

export const CONNECTOR_GUIDE_UPDATED = "15 October 2026";
export const CONNECTOR_MCP_URL = "https://cherryclipz-v2-backend.onrender.com/mcp";

export const CONNECTOR_GUIDE_TITLE = "CherryClipz in Claude and ChatGPT";
export const CONNECTOR_GUIDE_INTRO =
  "Connect CherryClipz to Claude or ChatGPT once. Then ask, in plain words, for the clips you want, and watch them land in your CherryClipz editor.";

/** The short film at the top of the page: a real ChatGPT session driving a real editor. */
export const CONNECTOR_VIDEO = {
  src: "/media/connect/asked-chatgpt.mp4",
  poster: "/media/connect/asked-chatgpt-poster.jpg",
  caption: "ChatGPT finds every line about art, puts them in order and exports the cut.",
};

export const CONNECTOR_CONNECT_HEADING = "Connect it in three steps";
export const CONNECTOR_CONNECT_NOTE =
  "CherryClipz is waiting for approval in both assistants' app directories. Until then, add it by hand with the address below.";
export const CONNECTOR_ADDRESS_LABEL = "Server address";
export const CONNECTOR_COPY_LABEL = "Copy";
export const CONNECTOR_COPIED_LABEL = "Copied";
export const CONNECTOR_IN_CHAT_LABEL = "In a chat";

export interface ConnectorApp {
  id: "claude" | "chatgpt";
  name: string;
  steps: string[];
  /** How to switch CherryClipz on inside one conversation. */
  inChat: string;
  openUrl: string;
  openLabel: string;
}

// Click paths checked against each assistant's own help pages on 15 October 2026
// (support.claude.com "custom connectors using remote MCP"; developers.openai.com
// "Connect and test your plugin"). Both apps rename menus often: re-check before editing.
export const CONNECTOR_APPS: ConnectorApp[] = [
  {
    id: "claude",
    name: "Claude",
    steps: [
      "In Claude, open Customize → Connectors.",
      "Click + Add → Add custom connector. Name it CherryClipz, paste the server address and follow the prompts to add it.",
      "Sign in with your CherryClipz email and the code we email you, then click Allow.",
    ],
    inChat: "Click + → Connectors and switch CherryClipz on.",
    openUrl: "https://claude.ai",
    openLabel: "Open Claude",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    steps: [
      "Open ChatGPT's Plugins page, click + and choose Add custom MCP server. Don't see it? Turn on Developer mode first, in Settings → Security and login.",
      "Name it CherryClipz, paste the server address, choose OAuth sign-in, confirm the warning and create it.",
      "Sign in with your CherryClipz email and the code we email you, then click Allow.",
    ],
    inChat: "Start a new chat, type @ and pick CherryClipz.",
    openUrl: "https://chatgpt.com/plugins",
    openLabel: "Open ChatGPT Plugins",
  },
];

export const CONNECTOR_ASK_HEADING = "Then just ask";
export const CONNECTOR_ASK_STEPS: string[] = [
  "Open your video at app.cherryclipz.com and switch on 🔗 AI link in the editor's AI bubble.",
  "Ask the assistant for the clips you want, in plain words.",
  "Its picks land in your editor in order, pinned to the top. Play them, change them, export them. Or ask it to export: the videos appear in My Library → Exported.",
];
export const CONNECTOR_EXAMPLES_LABEL = "Things to ask";
export const CONNECTOR_EXAMPLES: string[] = [
  "“List my CherryClipz videos.”",
  "“In my video ‘Interview 1’, pick the five strongest lines about the move and put them in my editor.”",
  "“From that first clip keep only ‘we started from nothing’ and ‘it was worth it’. Skip the middle.”",
  "“Make a 60-second cut that opens on a hook and ends on a line that lands.”",
  "“Export those as vertical videos with captions, and don't auto-post.”",
];

export const CONNECTOR_DETAILS_HEADING = "The details";

export const CONNECTOR_GUIDE_SECTIONS: LegalSection[] = [
  {
    heading: "What it can do",
    bullets: [
      "List your videos and read a video's clips: every spoken line, with its speaker, mood and length.",
      "Pick clips and put them in play order in your open CherryClipz editor, or in the Premiere Pro panel.",
      "Trim a clip to the exact words it quotes, or cut one clip into several pieces: skip a word, or drop the middle of a long line.",
      "Export clips as videos to My Library → Exported: one video per clip or one video of all of them, with or without captions, in the original shape or vertical.",
      "Import a video from a link, and make a share link to one of your videos.",
    ],
  },
  {
    heading: "Good to know",
    bullets: [
      "Sign in to the assistant with the same CherryClipz email you use in your browser, or its picks won't show in your editor.",
      "The assistant picks, orders and splits clips; it never deletes anything from your library.",
      "Very long videos (a thousand clips or more) take the assistant a few reads.",
      "Each export counts toward your plan's monthly exports, the same as an export from the website.",
    ],
  },
];

/**
 * The same page as plain sections, for the no-JavaScript copy (public/ai.html, built by
 * scripts/build-static-pages.js): the steps, the asks, then the details.
 */
export const CONNECTOR_STATIC_SECTIONS: LegalSection[] = [
  ...CONNECTOR_APPS.map((app) => ({
    heading: `Connect it to ${app.name}`,
    bullets: [
      `${CONNECTOR_ADDRESS_LABEL}: ${CONNECTOR_MCP_URL}`,
      ...app.steps,
      `${CONNECTOR_IN_CHAT_LABEL}: ${app.inChat}`,
    ],
  })),
  { heading: CONNECTOR_ASK_HEADING, bullets: CONNECTOR_ASK_STEPS },
  { heading: CONNECTOR_EXAMPLES_LABEL, bullets: CONNECTOR_EXAMPLES },
  ...CONNECTOR_GUIDE_SECTIONS,
];

/** The home page band that sends people here (pages/homepage/components/FlagshipPage.tsx). */
export const CONNECTOR_HOME_BAND = {
  eyebrow: "Claude · ChatGPT",
  heading: "Edit by asking Claude or ChatGPT.",
  body: "Already use one? Connect CherryClipz once, then ask for the clips you want. They land in your editor, in order.",
  cta: "How to connect",
};
